import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { cashSummary, type CashMovementType, type CashSummary } from '@pos/core';
import {
  getMeta,
  rejectedAccountEntryIds,
  setMeta,
  type AccountEntryRow,
  type CashMovementRow,
  type CashSessionRow,
  type RegisterRow,
  type SaleRow,
} from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { supabase } from '../../lib/supabase.ts';
import { useOnline } from '../../lib/use-online.ts';
import { useSyncStatus } from '../../services/sync.ts';

// Cajas compartidas: el turno se abre en una CAJA y lo usan todos los dispositivos del negocio.
// Cada dispositivo usa una caja (la única, o la que eligió). Si se abrió una caja sin conexión,
// esa es solo de este dispositivo (device_only) y se usa primero hasta cerrarla.

/** Cajas del negocio que se pueden usar (sin las eliminadas), la principal primero. */
export function useRegisters(): RegisterRow[] | undefined {
  const { business } = useSession();
  return useLiveQuery(
    async () =>
      (await db.registers.where('business_id').equals(business.id).toArray())
        .filter((r) => !r.archived_at)
        .sort((a, b) => Number(b.is_default) - Number(a.is_default) || a.name.localeCompare(b.name, 'es')),
    [business.id],
  );
}

/** Caja que usa este dispositivo: la elegida (si sigue existiendo), si no la principal o la única. */
export function useCurrentRegister(): RegisterRow | null | undefined {
  const registers = useRegisters();
  const chosen = useLiveQuery(() => getMeta(db, 'cash_register'), []);
  if (registers === undefined) return undefined;
  if (registers.length === 0) return null;
  return registers.find((r) => r.id === chosen) ?? registers.find((r) => r.is_default) ?? registers[0]!;
}

export function chooseRegister(registerId: string): Promise<void> {
  return setMeta(db, 'cash_register', registerId);
}

/**
 * Turno de caja que usa ESTE dispositivo (null = cerrada; undefined = cargando): primero una caja
 * abierta solo en este dispositivo (sin conexión); si no, la caja compartida abierta.
 */
export function useOpenSession(): CashSessionRow | null | undefined {
  const { device, business } = useSession();
  const register = useCurrentRegister();
  return useLiveQuery(async () => {
    if (register === undefined) return undefined;
    const open = await db.cash_sessions.where('business_id').equals(business.id).filter((s) => s.closed_at === null).toArray();
    const mine = open.find((s) => s.device_only && s.device_id === device.id);
    if (mine) return mine;
    const shared = open.filter((s) => !s.device_only);
    // Todavía no llegaron las cajas del servidor (recién actualizada la app, sin conexión): se usa
    // la abierta en este dispositivo o, si no, cualquier compartida.
    if (!register) return shared.find((s) => s.device_id === device.id) ?? shared[0] ?? null;
    // Turnos anteriores a las cajas compartidas no tienen device_only: cuentan como compartidos.
    return shared.find((s) => (s.register_id ?? register.id) === register.id) ?? null;
  }, [device.id, business.id, register?.id, register === undefined]);
}

export interface SessionData {
  sales: SaleRow[];
  movements: CashMovementRow[];
  accountPayments: AccountEntryRow[];
}

/** Ventas, movimientos y cobros de cuenta corriente de una caja hechos en ESTE dispositivo. */
export function useSessionData(sessionId: string | null | undefined): SessionData | undefined {
  return useLiveQuery(async () => {
    if (!sessionId) return { sales: [], movements: [], accountPayments: [] };
    const [sales, movements, entries, rejected] = await Promise.all([
      db.sales.where('cash_session_id').equals(sessionId).toArray(),
      db.cash_movements.where('session_id').equals(sessionId).toArray(),
      db.account_entries.where('cash_session_id').equals(sessionId).toArray(),
      rejectedAccountEntryIds(db),
    ]);
    return { sales, movements, accountPayments: entries.filter((e) => e.type === 'payment' && !rejected.has(e.id)) };
  }, [sessionId]);
}

export function useCashSummary(session: CashSessionRow | null | undefined, data: SessionData | undefined): CashSummary | null {
  return useMemo(() => {
    if (!session || !data) return null;
    return cashSummary(
      session.opening_amount,
      data.sales.map((s) => ({ kind: s.kind, payments: s.payments })),
      data.movements,
      data.accountPayments.map((e) => ({ method: e.method, amount: e.amount })),
    );
  }, [session, data]);
}

export interface ServerMovement {
  id: string;
  type: CashMovementType;
  amount: number;
  reason: string;
  occurred_at: string;
  created_by: string | null;
  device_id: string | null;
}

/** Estado calculado por el servidor con TODOS los dispositivos. expected = null con arqueo ciego. */
export interface ServerCashSummary extends Omit<CashSummary, 'expected'> {
  expected: number | null;
  movements: ServerMovement[];
  blind: boolean;
}

export async function fetchCashSummary(sessionId: string): Promise<ServerCashSummary> {
  const { data, error } = await supabase.rpc('cash_session_summary', { p_session_id: sessionId });
  if (error) throw new Error(error.message);
  return data as ServerCashSummary;
}

/**
 * Estado de la caja compartida desde el servidor (todos los dispositivos). Se actualiza al
 * sincronizar y cada 30 segundos. null = sin conexión o caja de un solo dispositivo.
 */
export function useServerCashSummary(session: CashSessionRow | null | undefined): {
  summary: ServerCashSummary | null;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const online = useOnline();
  const sync = useSyncStatus();
  const [summary, setSummary] = useState<ServerCashSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const shared = !!session && !session.device_only;
  const id = session?.id;

  const refresh = useCallback(async () => {
    if (!id || !shared || !navigator.onLine) return;
    try {
      setSummary(await fetchCashSummary(id));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [id, shared]);

  useEffect(() => {
    setSummary(null);
    setError(null);
  }, [id]);
  useEffect(() => {
    if (!online || !shared) return;
    void refresh();
    const t = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(t);
  }, [online, shared, refresh, sync.lastSyncAt]);

  return { summary: online && shared ? summary : null, error, refresh };
}

/** Último turno cerrado de la caja que usa este dispositivo (para sugerir el fondo). */
export function useLastClosedSession(): CashSessionRow | undefined {
  const { business } = useSession();
  const register = useCurrentRegister();
  return useLiveQuery(async () => {
    const all = await db.cash_sessions
      .where('business_id')
      .equals(business.id)
      .filter((s) => s.closed_at !== null && (!register || (s.register_id ?? register.id) === register.id))
      .toArray();
    return all.sort((a, b) => (b.closed_at ?? '').localeCompare(a.closed_at ?? ''))[0];
  }, [business.id, register?.id]);
}

/** Nombre de una caja (para listas e informes). */
export function useRegisterNames(): (id: string | null | undefined) => string {
  const { business } = useSession();
  const all = useLiveQuery(() => db.registers.where('business_id').equals(business.id).toArray(), [business.id]);
  return useCallback(
    (id: string | null | undefined) => all?.find((r) => r.id === id)?.name ?? all?.find((r) => r.is_default)?.name ?? 'Caja',
    [all],
  );
}
